import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FilterChips } from './FilterChips.tsx';
import { FilterSidebar } from './FilterSidebar.tsx';
import { Icon } from './Icon.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { AccessBadge } from './AccessBadge.tsx';
import { HelpPanel } from './HelpPanel.tsx';
import { loadFiltersFromUrl } from '../stores/filter-store.ts';

interface Props {
  user: { display_name: string; role: string } | null;
  children: React.ReactNode;
}

export function AppShell({ user, children }: Props) {
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    loadFiltersFromUrl();
    // Theme aus System-Pref oder localStorage
    const stored = localStorage.getItem('stat:theme');
    const isDark =
      stored === 'dark' ||
      (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setDark(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    localStorage.setItem('stat:theme', next ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', next);
  }

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:bg-slate-900/95 dark:supports-[backdrop-filter]:bg-slate-900/80 border-b border-slate-200/80 dark:border-slate-800/80 px-4 h-14 flex items-center gap-3 shrink-0 sticky top-0 z-30">
        <a
          href="/"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors group"
          title="Zurück zum DRS-Cockpit"
        >
          <Icon name="chevronRight" size={14} className="rotate-180 group-hover:-translate-x-0.5 transition-transform" />
          Cockpit
        </a>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-800" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-soft">
            <Icon name="trendingUp" size={15} />
          </div>
          <h1 className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Statistik
          </h1>
        </div>

        <nav className="flex items-center gap-0.5 ml-3">
          <NavTab to="/" current={loc.pathname === '/'} icon="dashboard">
            Dashboard
          </NavTab>
          <NavTab to="/pivot" current={loc.pathname === '/pivot'} icon="table">
            Pivot
          </NavTab>
          <NavTab to="/reports" current={loc.pathname === '/reports'} icon="reports">
            Berichte
          </NavTab>
          <NavTab to="/periodenvergleich" current={loc.pathname === '/periodenvergleich'} icon="trendingUp">
            Vergleich
          </NavTab>
          {user?.role === 'admin' && (
            <NavTab to="/admin" current={loc.pathname === '/admin'} icon="settings">
              Admin
            </NavTab>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
            onClick={() => {
              // Trigger command palette via key event
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
            }}
            title="Schnellsuche öffnen (Strg+K)"
          >
            <Icon name="search" size={13} />
            <span>Suchen…</span>
            <kbd className="kbd">⌘K</kbd>
          </button>
          <button
            className="btn-icon text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={() => setSidebarOpen((s) => !s)}
            title={sidebarOpen ? 'Filter ausblenden' : 'Filter einblenden'}
          >
            <Icon name="panelLeft" size={18} />
          </button>
          <button
            className="btn-icon text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={toggleTheme}
            title="Theme wechseln"
          >
            {dark ? '☀' : '☾'}
          </button>
          <HelpPanel />
          <div className="w-px h-5 bg-slate-200 dark:bg-slate-800" />
          <AccessBadge />
          <div className="w-px h-5 bg-slate-200 dark:bg-slate-800" />
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {user?.display_name}
          </span>
        </div>
      </header>

      {/* Filter Chips */}
      <FilterChips />

      <CommandPalette />

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && <FilterSidebar onClose={() => setSidebarOpen(false)} />}
        <main className="flex-1 overflow-auto p-4 lg:p-6 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}

function NavTab({
  to,
  current,
  icon,
  children,
}: {
  to: string;
  current: boolean;
  icon: 'dashboard' | 'table' | 'reports' | 'settings' | 'trendingUp';
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`nav-tab flex items-center gap-1.5 ${
        current ? 'nav-tab-active' : 'nav-tab-inactive'
      }`}
    >
      <Icon name={icon} size={15} />
      {children}
    </Link>
  );
}
